import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import {
  PageContainer,
  Card,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  formatDate,
  fadeIn,
  ButtonSuccess,
  ToggleInput
} from '../styles/PageStyles';
import { faHourglassEmpty } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../components/common/Pagination';
import styled from 'styled-components';
import { fetchTaskCount, makeReserveTasks } from '../services/TaskService';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/common/ToastProvider';
import { useTaskList } from '../hooks/useTaskList';
import EmptyState from '../components/common/EmptyState';

const TabsContainer = styled.div`
  display: flex;
  // border-bottom: 1px solid rgba(0, 102, 255, 0.1);
  margin-bottom: 1.5rem;
`;

const ModalTab = styled.button`
  padding: 0.8rem 1.2rem;
  background: transparent;
  border: none;
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${props => props.active ? 'var(--primary)' : 'transparent'};
    transition: all 0.2s ease;
  }
  
  &:hover {
    color: var(--primary);
  }
`;

  // Get status badge color
const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'active';
      case 'connected': return 'active';
      case 'archived': return 'default';
      case 'disconnected': return 'error';
      default: return 'default';
    }
  };

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'closed') return 'rgba(40, 167, 69, 0.1)';
    if (props.status === 'inactive') return 'rgba(220, 53, 69, 0.1)';
    if (props.status === 'waiting') return 'rgba(255, 193, 7, 0.1)';
    return 'rgba(0, 102, 255, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'closed') return '#34C759';
    if (props.status === 'inactive') return '#FF3B30';
    if (props.status === 'waiting') return '#FFCC00';
    return 'var(--primary)';
  }};
`;

const calculateSlaDifference = (createdTimestamp, sla, slaUnit) => {
    const createdDate = new Date(createdTimestamp);
    const currentDate = new Date();

    const slaHourMs = sla * 60 * 60 * 1000;
    const slaMinuteMs = sla * 60 * 1000;
    const slaDayMs = 24 * slaHourMs;

    const slaMs = slaUnit === "Minutes" ? slaMinuteMs : slaUnit === "Hours" ? slaHourMs : slaDayMs;

    const deadlineTimeMs = createdDate.getTime() + slaMs;
    const deadlineDate = new Date(deadlineTimeMs);

    const remainingTimeMs = deadlineTimeMs - currentDate.getTime();

    const sign = remainingTimeMs >= 0 ? 'Remaining' : 'Overdue';
    const absoluteMs = Math.abs(remainingTimeMs);

    const totalSeconds = Math.floor(absoluteMs / 1000);
    const seconds = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;
    const days = Math.floor(totalHours / 24);

    // let remainingTimeDisplay;
    // if (days > 0) remainingTimeDisplay = `${days}d `;
    // if (hours > 0 || days > 0) remainingTimeDisplay += `${hours}h `;
    // if (minutes > 0 || hours > 0 || days > 0) remainingTimeDisplay += `${minutes}m `;
    // remainingTimeDisplay += `${seconds}s`;

    let parts = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    if (minutes || hours || days) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);

    let remainingTimeDisplay = parts.join(" ");


    return {
        deadline: deadlineDate.toISOString(),
        status: sign,
        remainingTimeDisplay: remainingTimeDisplay.trim()
    };
};

const WaitingList = ({ 
  toReservArr, 
  setToReserveArr,
  data,
  loading,
  currentPage,
  setCurrentPage,
  onPageChange,
  onViewTask,
  totalPages,
  setTotalPages,
  totalCount
}) => {
  if (loading) return <div>Loading...</div>;

  if(!data || data.length === 0) {
    return <EmptyState 
                heading="No Task found" 
                description="You don't have any tasks assigned right now. Start by creating a new task or check back later for updates."
                 />
  }

  return (
    <>
      <Card>
        <Table>
          <thead>
            <tr>
              <TableHeader>
                <ToggleInput
                  type="checkbox"
                  checked={
                    data.length > 0 && 
                    data.length === toReservArr.length
                  }
                  onChange={(e) => {
                    setToReserveArr(e.target.checked 
                      ? data.map(task => task.response.task_id)
                      : []
                    );
                  }}
                />
              </TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Last Updated</TableHeader>
              <TableHeader>Task Deadline</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {data.map((task) => (
              <TableRow key={task.response.task_id}>
                <TableCell>
                  <ToggleInput
                    type="checkbox"
                    checked={toReservArr.includes(task.response.task_id)}
                    onChange={(e) => {
                      const id = task.response.task_id;
                      setToReserveArr(prev => 
                        e.target.checked
                          ? [...prev, id]
                          : prev.filter(ele => ele !== id)
                      );
                    }}
                  />
                </TableCell>
                <TableCell>{task.response.task_id}</TableCell>
                <TableCell>{task.response.task_description}</TableCell>
                <TableCell>{formatDate(task.updated_at)}</TableCell>
                <TableCell>
                  {task.response.task_status !== "CLOSED" ? (
                    <span
                      style={{
                        padding: '8px 6px',
                        background: `${calculateSlaDifference(task?.response?.task_created_at, task?.response?.task_data?.sla, task?.response?.task_data?.sla_unit).status === "Remaining" ? '#eafae3' : '#fde8db'}`,
                        borderRadius: '20px'
                      }}
                    >
                      {calculateSlaDifference(task?.response?.task_created_at, task?.response?.task_data?.sla, task?.response?.task_data?.sla_unit).remainingTimeDisplay}
                    </span>
                  ) : <span>-</span>}

                </TableCell>
                <TableCell>
                  <StatusBadge status={getStatusColor(task.response.status)}>
                    {task.response.task_status}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button 
                      primary 
                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} 
                      onClick={() => onViewTask(task.response.task_id)}
                    >
                      View
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </Card>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={onPageChange}
        setTotalPages={setTotalPages}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
};

const ReservedList = ({ 
  data,
  loading,
  currentPage,
  setCurrentPage,
  onPageChange,
  onViewTask,
  totalPages,
  setTotalPages,
  totalCount
}) => {
  if (loading) return <div>Loading...</div>;

   if(!data || data.length === 0) {
    return <EmptyState 
                heading="No Task found" 
                description="You don't have any tasks assigned right now. Start by creating a new task or check back later for updates."
                 />
  }

  return (
    <>
      <Card>
        <Table>
          <thead>
            <tr>
              <TableHeader>Name</TableHeader>
              <TableHeader>Description</TableHeader>
              <TableHeader>Last Updated</TableHeader>
              <TableHeader>Task Deadline</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {data.map((task) => (
              <TableRow key={task.response.task_id}>
                <TableCell>{task.response.task_id}</TableCell>
                <TableCell>{task.response.task_description}</TableCell>
                <TableCell>{formatDate(task.updated_at)}</TableCell>
                <TableCell>
                  {
                    task.response.task_status !== "CLOSED" ? (
                      <span
                        style={{
                          padding: '8px 6px',
                          background: `${calculateSlaDifference(task?.response?.task_created_at, task?.response?.task_data?.sla, task?.response?.task_data?.sla_unit).status === "Remaining" ? '#eafae3' : '#fde8db'}`,
                          borderRadius: '20px'
                        }}
                      >
                        {calculateSlaDifference(task?.response?.task_created_at, task?.response?.task_data?.sla, task?.response?.task_data?.sla_unit).remainingTimeDisplay}
                      </span>
                    ) :
                      <spa>-</spa>
                  }

                </TableCell>
                <TableCell>
                  <StatusBadge status={getStatusColor(task.response.status)}>
                    {task.response.task_status}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button 
                      primary 
                      style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }} 
                      onClick={() => onViewTask(task.response.task_id)}
                    >
                      View
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </Card>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={onPageChange}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
      />
    </>
  );
};

const TaskList = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  
  const [activeTabIdx, setActiveTabIdx] = useState('waiting');
  const [toReservArr, setToReserveArr] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch both lists
  const waiting = useTaskList(
    'WAITING',
    user?.companyCd,
    user?.groups,
    `${user?.firstname} ${user?.lastname}`
  );

  const reserved = useTaskList(
    'RESERVED',
    user?.companyCd,
    user?.groups,
    `${user?.firstname} ${user?.lastname}`
  );

  const getWaitingCount = async () => {
    const query = {
      filter: {
        response: {
          "assignment_status": "WAITING"
        }
      }
    }
    const count = await fetchTaskCount(query);
    waiting.setTotalCount(count)
  }

  const getReservedCount = async () => {
    const query = {
      filter: {
        response: {
          "assignment_status": "RESERVED"
        }
      }
    }
    const count = await fetchTaskCount(query);
    reserved.setTotalCount(count)

  }

  useEffect(() => {
    waiting.fetchData(waiting.currentPage);
    getWaitingCount();

  }, [waiting.currentPage, user?.companyCd])

  useEffect(() => {
    reserved.fetchData(reserved.currentPage);
    getReservedCount();

  }, [reserved.currentPage, user?.companyCd])

  const activeList = activeTabIdx === 'waiting' ? waiting : reserved;

  const handlePageChange = (newPage) => {
    console.log("page ch")
    activeList.fetchData(newPage);
  };



  const handleViewTask = (taskId) => {
    navigate(`/inbox-details/${taskId}`);
  };

  const handleReservedTask = async () => {
    if (!toReservArr.length) {
      showToast({
        message: "Please select at least one task to reserve.",
        type: "error",
        duration: 4000
      });
      return;
    }

    try {
      const payload = { task_id: toReservArr };
      const { status, reserved_tasks, already_reserved_tasks } = 
        await makeReserveTasks(payload);

      if (status !== "SUCCESS") {
        showToast({
          message: "Failed to reserve task. Please try again.",
          type: "error"
        });
        return;
      }

      if (reserved_tasks?.length > 0) {
        showToast({
          message: "Task reserved successfully.",
          type: "success",
          duration: 4000
        });
      }

      if (already_reserved_tasks?.length > 0) {
        const reservedIds = already_reserved_tasks
          .map(task => task.task_id)
          .join(', ');
        showToast({
          message: `Note: Tasks ${reservedIds} were already reserved.`,
          type: "warning",
          duration: 6000
        });
      }

      // Refresh the waiting list
      await waiting.fetchData(1);
      setToReserveArr([]);
    } catch (error) {
      showToast({
        message: "An error occurred while reserving tasks.",
        type: "error"
      });
    }
  };

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <PageHeaderWithInfo
          icon={faHourglassEmpty}
          title="Task List"
          description="Manage and update the task."
        >
          <SearchInput
            type="text"
            placeholder="Search Tasks"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </PageHeaderWithInfo>

        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <TabsContainer>
            <ModalTab
              active={activeTabIdx === 'waiting'}
              onClick={() => setActiveTabIdx('waiting')}
            >
              Group Tasks
            </ModalTab>
            <ModalTab
              active={activeTabIdx === 'reserved'}
              onClick={() => setActiveTabIdx('reserved')}
            >
              My Tasks
            </ModalTab>
          </TabsContainer>

          {activeTabIdx === 'waiting' && (
            <ButtonSuccess 
              primary 
              style={{ height: 'fit-content' }} 
              onClick={handleReservedTask}
            >
              Reserve
            </ButtonSuccess>
          )}
        </div>

        {activeTabIdx === 'waiting' && (
          <WaitingList
            toReservArr={toReservArr}
            setToReserveArr={setToReserveArr}
            data={waiting.data}
            loading={waiting.loading}
            currentPage={waiting.currentPage}
            setCurrentPage={waiting.setCurrentPage}
            totalPages={waiting.totalPages}
            setTotalPages={waiting.setTotalPages}
            totalCount={waiting.totalCount}
            onPageChange={handlePageChange}
            onViewTask={handleViewTask}
          />
        )}

        {activeTabIdx === 'reserved' && (
          <ReservedList
            data={reserved.data}
            loading={reserved.loading}
            currentPage={reserved.currentPage}
            setCurrentPage={reserved.setCurrentPage}
            totalPages={reserved.totalPages}
            totalCount={reserved.totalCount}
            setTotalPages={reserved.setTotalPages}
            onPageChange={handlePageChange}
            onViewTask={handleViewTask}
          />
        )}
      </motion.div>
    </PageContainer>
  );
};

export default TaskList;
